import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  InputNumber, 
  Button, 
  Switch, 
  Upload, 
  message, 
  Space, 
  Divider,
  Typography,
  Row,
  Col,
  Spin,
  Alert
} from 'antd';
import { 
  SaveOutlined, 
  LockOutlined, 
  UploadOutlined, 
  QrcodeOutlined,
  DollarOutlined,
  BankOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { settingsService } from '../services/api';
import { authService } from '../services/authService';

const { Title, Text } = Typography;

const Settings: React.FC = () => {
  const [settingsForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [qrCodeImage, setQrCodeImage] = useState<string>('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsService.get();
      const settingsData = response.data.data.settings;
      settingsForm.setFieldsValue(settingsData);
      setQrCodeImage(settingsData.qrCodeImage || '');
    } catch (error) {
      console.error('Error loading settings:', error);
      message.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsUpdate = async (values: any) => {
    try {
      setSaving(true);
      await settingsService.update(values);
      message.success('Settings updated successfully');
    } catch (error) {
      message.error('Error updating settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (values: any) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('New password and confirm password do not match');
      return;
    }
    try {
      setChangingPassword(true);
      const response = await authService.changePassword(
        values.currentPassword,
        values.newPassword
      );
      if (response.success) {
        message.success('Password updated successfully');
        passwordForm.resetFields();
      } else {
        message.error(response.message || 'Error updating password');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Error updating password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleQRUpload = async (file: File) => {
    try {
      const response = await settingsService.uploadQRCode(file);
      setQrCodeImage(response.data.data.qrCodeUrl);
      message.success('QR Code uploaded successfully');
    } catch (error) {
      message.error('Error uploading QR code');
    }
    return false; // Prevent default upload
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <Title level={2} className="mb-6 flex items-center gap-2">
          <SettingOutlined className="text-blue-600" />
          Settings
        </Title>

        <Space direction="vertical" size="large" className="w-full">
          {/* Penalty Settings */}
          <Card
            title={
              <span className="flex items-center gap-2">
                <DollarOutlined className="text-green-600" />
                Penalty Settings
              </span>
            }
            className="shadow-lg border-0 rounded-xl"
          >
            <Form
              form={settingsForm}
              layout="vertical"
              onFinish={handleSettingsUpdate}
              initialValues={{
                penaltyPercentage: 5,
                gracePeriodDays: 30,
              }}
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Penalty Percentage (%)"
                    name="penaltyPercentage"
                    rules={[
                      { required: true, message: 'Please enter penalty percentage' },
                      { type: 'number', min: 0, max: 100, message: 'Must be between 0 and 100' },
                    ]}
                  >
                    <InputNumber
                      className="w-full"
                      min={0}
                      max={100}
                      formatter={(value) => `${value}%`}
                      parser={(value) => {
                        const parsed = value?.replace('%', '') || '';
                        const num = parsed ? parseFloat(parsed) : 0;
                        return (isNaN(num) ? 0 : num) as any;
                      }}
                      placeholder="Enter penalty percentage"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Grace Period (Days)"
                    name="gracePeriodDays"
                    rules={[
                      { required: true, message: 'Please enter grace period' },
                      { type: 'number', min: 0, message: 'Must be a positive number' },
                    ]}
                  >
                    <InputNumber
                      className="w-full"
                      min={0}
                      placeholder="Enter grace period in days"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>

          {/* Maintenance Fee Settings */}
          <Card
            title={
              <span className="flex items-center gap-2">
                <DollarOutlined className="text-blue-600" />
                Maintenance Fee Settings
              </span>
            }
            className="shadow-lg border-0 rounded-xl"
          >
            <Form
              form={settingsForm}
              layout="vertical"
              onFinish={handleSettingsUpdate}
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Maintenance Fee Per Square Feet (₹)"
                    name="maintenanceFeePerSquareFeet"
                    rules={[
                      { required: true, message: 'Please enter maintenance fee per square feet' },
                      { type: 'number', min: 0, message: 'Must be a positive number' },
                    ]}
                    tooltip="This value is used to calculate maintenance fees for shops. Formula: squareFeet × rate × periodMonths"
                  >
                    <InputNumber
                      className="w-full"
                      min={0}
                      step={0.01}
                      formatter={(value) => `₹ ${value}`}
                      parser={(value) => {
                        const parsed = value?.replace('₹ ', '').trim() || '';
                        const num = parsed ? parseFloat(parsed) : 0;
                        return (isNaN(num) ? 0 : num) as any;
                      }}
                      placeholder="Enter rate per square feet"
                    />
                  </Form.Item>
                  <Alert
                    message="Note"
                    description="This value replaces the hardcoded ₹3 per square feet. Maintenance fee is calculated as: squareFeet × rate × periodMonths"
                    type="info"
                    showIcon
                    className="mt-2"
                  />
                </Col>
              </Row>
            </Form>
          </Card>

          {/* Payment Account Details */}
          <Card
            title={
              <span className="flex items-center gap-2">
                <BankOutlined className="text-purple-600" />
                Payment Account Details
              </span>
            }
            className="shadow-lg border-0 rounded-xl"
          >
            <Form
              form={settingsForm}
              layout="vertical"
              onFinish={handleSettingsUpdate}
            >
              <Row gutter={24}>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Account Name"
                    name="accountName"
                    rules={[
                      { required: false },
                      { max: 100, message: 'Account name should not exceed 100 characters' },
                    ]}
                  >
                    <Input
                      className="w-full"
                      placeholder="Enter account holder name"
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Account Number"
                    name="accountNumber"
                    rules={[
                      { required: false },
                      { min: 10, message: 'Account number should be at least 10 characters' },
                    ]}
                  >
                    <Input
                      className="w-full"
                      placeholder="Enter bank account number"
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="IFSC Code"
                    name="ifscCode"
                    rules={[
                      { required: false },
                      { pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/, message: 'Invalid IFSC code format' },
                    ]}
                  >
                    <Input
                      className="w-full"
                      placeholder="Enter IFSC code"
                      size="large"
                      style={{ textTransform: 'uppercase' }}
                      onChange={(e) => {
                        settingsForm.setFieldsValue({ ifscCode: e.target.value.toUpperCase() });
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>

          {/* QR Code Management */}
          <Card
            title={
              <span className="flex items-center gap-2">
                <QrcodeOutlined className="text-orange-600" />
                QR Code Management
              </span>
            }
            className="shadow-lg border-0 rounded-xl"
          >
            <Form
              form={settingsForm}
              layout="vertical"
              onFinish={handleSettingsUpdate}
            >
              {qrCodeImage && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <Text className="block mb-2 text-gray-600">Current QR Code:</Text>
                  <img 
                    src={qrCodeImage} 
                    alt="QR Code" 
                    className="w-48 h-48 rounded-lg border-2 border-gray-200 shadow-md object-contain bg-white p-2"
                  />
                </div>
              )}
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Upload QR Code"
                    name="qrCode"
                  >
                    <Upload
                      beforeUpload={handleQRUpload}
                      accept="image/*"
                      showUploadList={false}
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />} size="large" className="w-full">
                        Click to Upload QR Code
                      </Button>
                    </Upload>
                    <Text type="secondary" className="block mt-2 text-xs">
                      Upload a QR code image for payment processing
                    </Text>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Enable QR Code"
                    name="qrCodeEnabled"
                    valuePropName="checked"
                  >
                    <Switch 
                      checkedChildren="Enabled" 
                      unCheckedChildren="Disabled"
                      size="default"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>

          {/* Save Settings Button */}
          <div className="flex justify-end">
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={() => settingsForm.submit()}
              className="bg-blue-600 hover:bg-blue-700 shadow-md"
            >
              Save All Settings
            </Button>
          </div>

          <Divider />

          {/* Password Change */}
          <Card
            title={
              <span className="flex items-center gap-2">
                <LockOutlined className="text-red-600" />
                Change Admin Password
              </span>
            }
            className="shadow-lg border-0 rounded-xl"
          >
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handlePasswordChange}
              className="max-w-2xl"
            >
              <Row gutter={24}>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Current Password"
                    name="currentPassword"
                    rules={[{ required: true, message: 'Please enter current password' }]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Enter current password"
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="New Password"
                    name="newPassword"
                    rules={[
                      { required: true, message: 'Please enter new password' },
                      { min: 6, message: 'Password must be at least 6 characters' },
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Enter new password"
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Confirm New Password"
                    name="confirmPassword"
                    dependencies={['newPassword']}
                    rules={[
                      { required: true, message: 'Please confirm new password' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('newPassword') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Passwords do not match'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Confirm new password"
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<LockOutlined />}
                  loading={changingPassword}
                  size="large"
                  className="bg-red-600 hover:bg-red-700"
                >
                  Change Password
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Space>
      </div>
    </div>
  );
};

export default Settings;
